import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import "./App.css";

// Public / auth pages — kept eager (first paint, small).
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import PendingPage from "./pages/auth/PendingPage";
import ForbiddenPage from "./pages/auth/ForbiddenPage";

// Foundation — eager (guard + shell + 404).
import RequireRole from "./components/RequireRole";
import DashLayout from "./dashboard/dashLayout";
import NotFound from "./components/modals/404";

// Dashboard + superadmin pages — code-split (loaded on first visit).
const DashboardHome = lazy(() => import("./pages/DashboardHome"));
const CustomersPage = lazy(() => import("./pages/customers/CustomersPage"));
const CustomerDetailPage = lazy(() => import("./pages/customers/CustomerDetailPage"));
const PaymentsPage = lazy(() => import("./pages/payments/PaymentsPage"));
const MobCustomersPage = lazy(() => import("./pages/mobile/MobCustomersPage"));
const MobPaymentsPage = lazy(() => import("./pages/mobile/MobPaymentsPage"));
const Complaints = lazy(() => import("./dashboard/complaints"));
const SettingsPage = lazy(() => import("./dashboard/settings"));
const MapsPage = lazy(() => import("./pages/MapsPage"));
const MessagesPage = lazy(() => import("./pages/messages/MessagesPage"));
const BillsPage = lazy(() => import("./pages/bills/BillsPage"));
const TrashPage = lazy(() => import("./pages/trash/TrashPage"));
const ReportsLayout = lazy(() => import("./dashboard/reports/ReportsLayout"));
const CollectionsReport = lazy(() => import("./dashboard/reports/collections"));
const HistoryReport = lazy(() => import("./dashboard/reports/history"));
const CustomersReport = lazy(() => import("./dashboard/reports/customers"));
const StocksReport = lazy(() => import("./dashboard/reports/stocks"));
const ActivityReport = lazy(() => import("./dashboard/reports/logs"));
const SuperAdminOverview = lazy(() => import("./pages/superadmin/SuperAdminOverview"));
const SuperAdminOrgs = lazy(() => import("./pages/superadmin/SuperAdminOrgs"));

/** Wrap a dashboard route so only org admins can reach it. */
const AdminOnly = ({ children }) => <RequireRole roles={["admin"]}>{children}</RequireRole>;

/** Fallback while a lazy route chunk loads. */
const RouteFallback = () => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
    <CircularProgress color="secondary" />
  </Box>
);

const App = () => {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* ---------- Public ---------- */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/pending" element={<PendingPage />} />
        <Route path="/forbidden" element={<ForbiddenPage />} />

        {/* ---------- Admin + Collector (org dashboard) ---------- */}
        <Route element={<RequireRole roles={["admin", "collector"]} />}>
          <Route path="/dashboard" element={<DashLayout />}>
            <Route index element={<DashboardHome />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/:id" element={<CustomerDetailPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="mob-customers" element={<MobCustomersPage />} />
            <Route path="mob-payments" element={<MobPaymentsPage />} />
            <Route path="complaints" element={<Complaints />} />
            <Route path="maps" element={<MapsPage />} />
            {/* Admin-only within the dashboard */}
            <Route path="bills" element={<AdminOnly><BillsPage /></AdminOnly>} />
            <Route path="messages" element={<AdminOnly><MessagesPage /></AdminOnly>} />
            <Route path="settings" element={<AdminOnly><SettingsPage /></AdminOnly>} />
            <Route path="trash" element={<AdminOnly><TrashPage /></AdminOnly>} />
            {/* Reports: Collections is open to collectors too (scoped to their
                own totals server-side — see reports.controller.js); the other
                report tabs stay admin-only. */}
            <Route path="reports" element={<ReportsLayout />}>
              <Route index element={<Navigate to="collections" replace />} />
              <Route path="collections" element={<CollectionsReport />} />
              <Route path="history" element={<AdminOnly><HistoryReport /></AdminOnly>} />
              <Route path="customers" element={<AdminOnly><CustomersReport /></AdminOnly>} />
              <Route path="stocks" element={<AdminOnly><StocksReport /></AdminOnly>} />
              <Route path="activity" element={<AdminOnly><ActivityReport /></AdminOnly>} />
            </Route>
          </Route>
        </Route>

        {/* ---------- Super Admin (platform) ---------- */}
        <Route element={<RequireRole roles={["superadmin"]} />}>
          <Route path="/superadmin" element={<DashLayout />}>
            <Route index element={<SuperAdminOverview />} />
            <Route path="orgs" element={<SuperAdminOrgs />} />
          </Route>
        </Route>

        {/* ---------- Fallbacks ---------- */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default App;
